import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Binder } from 'src/app/models/binder.model';
import { TaxDataService } from 'src/app/services/app/tax-data.service';
import { SkeletonModule } from 'primeng/skeleton';
import { InputNumberModule } from 'primeng/inputnumber';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { BinderService } from '@/services/api/binder.service';
import { BinderDialogService } from '@/services/app/binder-dialog.service';
import { UserAccessService } from '@/services/app/user-access.service';
import { ButtonModule } from 'primeng/button';

interface FinancialTooltip {
  planPrice: string;
optionTotal: string;
promoTotal: string;
travelSwitch: string;
travelRate: string;
subtotal: string;
taxRate: string;
taxReductions: string;
projectCost: string;
processingFee: string;
baseDesignFee: string;
engineeringFee: string;
totalDesignFee: string;
}

@Component({
    selector: 'app-binder-totals',
    imports: [
      SkeletonModule, 
      InputNumberModule, 
      CommonModule, 
      FormsModule, 
      TagModule, 
      TableModule,
      ToggleSwitchModule,
      TooltipModule,
      ButtonModule
    ],
    template: `
<div class="card flex flex-col gap-4">
    <div class="text-2xl font-bold">Binder Totals</div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.planPrice" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Plan Price</label>
        <p-inputNumber *ngIf="getBinder() && checkPriceChange()" [(ngModel)]="getBinder().PlanPrice"
            (ngModelChange)="onPlanPriceChange($event)" inputId="plan" mode="decimal" [minFractionDigits]="2"
            [maxFractionDigits]="5">
        </p-inputNumber>
        <div *ngIf="this.binderService.currentBinder().PlanPrice && !checkPriceChange()"
            class="text-900 w-full field-value md:w-7 md:flex-order-0 flex-order-1">
            {{getBinder().PlanPrice | currency}}
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.optionTotal" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Options Total</label>
        <div class="col-span-12 md:col-span-8">
            {{ getTotals().options.optionSubtotal| currency}}
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.travelSwitch" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0" [pTooltip]="tooltipValues.travelSwitch" tooltipPosition="left">Travel Rate ({{
            getBinder().ShowTravel ? 'Visible' : 'Hidden' }})</label>
        <div class="col-span-12 md:col-span-8">
            <p-toggleswitch [disabled]="!userAccessService.binderEditAccess()"
                [(ngModel)]="getBinder().ShowTravel" (onChange)="onShowTravelChange($event.checked)" />
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.travelRate" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Travel Rate</label>
        <div class="col-span-12 md:col-span-8">
            <p-inputNumber id="travel" [disabled]="!userAccessService.binderEditAccess()"
                [(ngModel)]="getBinder().Travel" (ngModelChange)="onTravelFeeChange($event)" inputId="travel"
                mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="5"
                class="w-full"
                [pTooltip]="tooltipValues.travelRate" tooltipPosition="bottom"
                [disabled]="!userAccessService.binderEditAccess()">
            </p-inputNumber>
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" *ngFor="let promo of getTotals().promos.binderPromos" [pTooltip]="tooltipValues.promoTotal" tooltipPosition="left">
        <label  class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">{{
            promo.promotions.promotionTitle }}</label>
        <div class="col-span-12 md:col-span-8">
            {{ promo.promotions.amount| currency}}
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.subtotal" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Pre-tax Subtotal</label>
        <div class="col-span-12 md:col-span-8">
            {{getTotals().subtotal| currency}}
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.taxRate" tooltipPosition="left">
    
        <label  class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">{{
            convertToPercent(getTax().rate/100) | number:
            '1.1-2'}}% Tax Rate </label>
        <div class="col-span-12 md:col-span-8">

          <button pButton [severity]="'secondary'" (click)="openCriteriaEditor()" style="justify-content: flex-start !important;" [disabled]="!userAccessService.binderEditAccess()" class="w-full col-span-12 md:col-span-10 ml-0 border-round shadow p-2 mb-1 text-left" style="border-radius: var(--border-radius);">
            <div class="flex justify-between items-center w-full">
                <div class="flex-1 text-left">
                    <div class="block text-900 text-left mb-1">{{getTotals().tax | currency}}</div>
                </div>
                <div class="flex items-center ml-2 mr-2" *ngIf="userAccessService.binderEditAccess()">
                    <i [class]="getTax().taxError?.showError ? 'pi pi-exclamation-triangle' : 'pi pi-pencil'" [style]="getTax().taxError?.showError ? 'color: red;' : 'color: var(--primary-color);'" [pTooltip]="tooltipContent"></i>
                </div>
            </div>
</button>


            <ng-template #tooltipContent>
                <div class="flex align-items-center" *ngIf="getTax().taxError?.showError">
                    <span> <b>Tax Lookup Failed:</b> {{taxError.message}} </span>
                </div>
                <div class="flex align-items-center" *ngIf="!getTax().taxError?.showError">
                    <span> {{getTax().rate | number: '1.1-2'}}% Tax Rate </span>
                </div>
            </ng-template>
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.taxReductions" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Tax Reductions</label>
        <div class="col-span-12 md:col-span-8">
            ({{ getReductions() | currency}})
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.projectCost" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0 font-semibold">Lexar Project Cost</label>
        <div class="col-span-12 md:col-span-8 font-semibold">
            {{getTotals().contractTotal | currency}}
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.processingFee" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Less Processing
            Fee</label>
        <div class="col-span-12 md:col-span-8">
            <p-inputNumber [disabled]="!userAccessService.binderEditAccess()"
            class="w-full"
                [(ngModel)]="getBinder().Processing_Fee" (ngModelChange)="onProcessingFeeChange($event)"
                inputId="processing" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="5">
            </p-inputNumber>
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.baseDesignFee" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Base Design Fee</label>
        <div class="col-span-12 md:col-span-8">
            {{getTotals().design.initialDesign| currency}}
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2" [pTooltip]="tooltipValues.engineeringFee" tooltipPosition="left">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Engineering</label>
        <div class="col-span-12 md:col-span-8">
            <div>

                <!-- <button pButton pRipple
                    *ngIf="userAccessService.binderEditAccess()" (click)="openEngineeringEditor()" type="button"
                    icon="pi pi-pencil" class="p-button-rounded p-button-text w-full" styleClass="w-full !text-left">
                    {{getTotals().options.engineeringTotal | currency}} 
                  </button> -->

                  <button pButton [severity]="'secondary'" (click)="openEngineeringEditor()" style="justify-content: flex-start !important;" [disabled]="!userAccessService.binderEditAccess()" class="w-full col-span-12 md:col-span-10 ml-0 border-round shadow p-2 mb-1 text-left" style="border-radius: var(--border-radius);">
            <div class="flex justify-between items-center w-full">
                <div class="flex-1 text-left">
                    <div class="block text-900 text-left mb-1">{{getTotals().options.engineeringTotal | currency}}</div>
                </div>
                <div class="flex items-center ml-2 mr-2" *ngIf="userAccessService.binderEditAccess()">
                    <i class="pi pi-pencil" style="color:var(--primary-color);"></i>
                </div>
            </div>
</button>
            </div>
        </div>
    </div>
    <div class="grid grid-cols-12 gap-2">
        <label class="flex items-center col-span-12 mb-2 md:col-span-4 md:mb-0">Total Design Fee</label>
        <div class="col-span-12 md:col-span-8">
            {{getTotals().design.designTotal | currency}}
        </div>
    </div>
</div>
    `
})
export class BinderTotalsComponent implements OnInit, OnDestroy {
  processingFeeControl = new FormControl('');
  travelFeeControl = new FormControl('');
  planPriceControl = new FormControl('');

  private debounceTimer: any;
  taxError: {
    showError: boolean,
    showDialog: boolean,
    message: string
  } = {
    showError: false,
    showDialog: false,
    message: ''
  }

  tooltipValues: FinancialTooltip = {
    travelSwitch: 'If hidden, the travel rate will not be visible on the contract. Instead, this amount will be added to the plan price.',
    travelRate: 'Enter a rate to compensate for extended travel time.',
    promoTotal: 'The total of all promotions applied to the project cost.',
    planPrice: 'Enter the price of the plan.',
optionTotal: 'All assemblies totaled',
subtotal: 'The sum of the Plan Price, Options Total, and Travel Rate',
taxRate: 'The tax rate applied to the subtotal based on settings in the design criteria.',
taxReductions: 'The total of all tax reductions (i.e. design service fee discounts) to be applied to the tax total.',
projectCost: 'The sum of the subtotal + tax - tax reductions',
processingFee: 'The non-refundable processing fee applied to the project cost, due upfront.',
baseDesignFee: 'The base design fee due to Lexar Corporate',
engineeringFee: 'Fee for additional engineering',
totalDesignFee: 'The total of the design fee and engineering fee, to be paid to Lexar Corporate',
  }

  constructor(
    public binderService: BinderService, 
    private binderDialogService: BinderDialogService,
    public userAccessService: UserAccessService,
    private confirmationService: ConfirmationService,
    public taxDataService: TaxDataService
  ) {}

  ngOnInit() {
    this.setInitialFormValues();
  }

  setInitialFormValues() {
    if(this.binderService.currentBinder().Processing_Fee) {
      this.processingFeeControl.setValue(this.binderService.currentBinder().Processing_Fee.toString(), { emitEvent: false });
    }
    if(this.binderService.currentBinder().Travel) {
      this.travelFeeControl.setValue(this.binderService.currentBinder().Travel?.toString() || '', { emitEvent: false });
    }
    if(this.binderService.currentBinder().PlanPrice) {
      this.planPriceControl.setValue(this.binderService.currentBinder().PlanPrice.toString(), { emitEvent: false });
    }
  }

  getReductions() {
    return this.getTotals().savings.reductions.reduce((total, reduction) => total + reduction.total, 0);
  }

  openEngineeringEditor() {
    this.binderDialogService.toggleDialog('Engineering Fee','engineering',true,'center',true)
  }

  openCriteriaEditor() {
    this.binderDialogService.toggleDialog('Design Criteria', 'design', true);
  }

  updateBinder(binder: Partial<Binder>) {
    console.log('binder patched', binder)
    this.binderService.updateBinder(this.binderService.currentBinder().BinderID,binder).subscribe((updatedBinder) => {
      this.binderService.patchBinder(updatedBinder);
    });
  }

  checkPriceChange() {
    if(this.userAccessService.binderEditAccess() && this.userAccessService.binderPlanPriceAccess()) {
      return true
    }
    return false
  }

  onShowTravelChange(value: boolean) {
    const binder = {
      ShowTravel: value
    }
    this.updateBinder(binder)
  }

  onProcessingFeeChange(value: number) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.updateBinder({ Processing_Fee: value });
    }, 500);
  }

  onTravelFeeChange(value: number) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.updateBinder({ Travel: value });
    }, 500);
  }

  onPlanPriceChange(value: number) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.updateBinder({ PlanPrice: value });
    }, 500);
  }

  convertToPercent(value: number | null) {
    if(value) {
      return value * 100
    }
    return 0
  }

  getBinder() {
    return this.binderService.currentBinder()
  }

  getTotals() {
    return this.binderService.totalsData()
  }

  getTax() {
    return this.taxDataService.taxData()
  }

  triggerTaxError(header:string,message:string) {
    this.confirmationService.confirm({
        header: header,
        icon: 'pi pi-exclamation-triangle',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptLabel: 'Retry',
        rejectLabel: 'Cancel',
        message: message,
        accept: () => {
            // this.retryTax.emit(true);
        },
        reject: () => {
            // this.taxError.showDialog=false;
        }
    });
}

ngOnDestroy() {
  clearTimeout(this.debounceTimer);
}

}
